import React from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  preferredLanguage: Yup.string().required('Preferred language is required'),
  notificationPreferences: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one notification method must be selected')
});

const optionalValidationSchema = Yup.object().shape({
  preferredLanguage: Yup.string(),
  notificationPreferences: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one notification method must be selected')
});

const Preferences = ({ initialData, onComplete, onBack, optional }) => {
  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Preferences</h2>
      <Formik
        initialValues={{
				  preferredLanguage: initialData.preferredLanguage || '',
				  notificationPreferences: initialData.notificationPreferences || []
        }}
        validationSchema={
					!optional ? validationSchema : optionalValidationSchema
				}
        onSubmit={onComplete}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='preferredLanguage' className={css(styles.label)}>
                Preferred Language
              </label>
              <Field name='preferredLanguage' className={css(styles.input)} />
              {errors.preferredLanguage && touched.preferredLanguage && (
                <div className={css(styles.error)}>
                  {errors.preferredLanguage}
                </div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>
                Notification Preferences
              </label>
              <div className={css(styles.checkboxGroup)}>
                <label className={css(styles.checkboxLabel)}>
                  <Field
                    type='checkbox'
                    name='notificationPreferences'
                    value='email'
                    className={css(styles.checkbox)}
                  />
                  Email
                </label>
                <label className={css(styles.checkboxLabel)}>
                  <Field
                    type='checkbox'
                    name='notificationPreferences'
                    value='sms'
                    className={css(styles.checkbox)}
                  />
                  SMS
                </label>
                <label className={css(styles.checkboxLabel)}>
                  <Field
                    type='checkbox'
                    name='notificationPreferences'
                    value='push'
                    className={css(styles.checkbox)}
                  />
                  Push Notifications
                </label>
              </div>
              {errors.notificationPreferences &&
								touched.notificationPreferences && (
  <div className={css(styles.error)}>
    {errors.notificationPreferences}
  </div>
              )}
            </div>
            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
								  styles.button,
								  optional && !(isValid && dirty) && styles.disabledButton
                )}
                whileHover={optional && isValid && dirty ? { scale: 1.05 } : {}}
                whileTap={optional && isValid && dirty ? { scale: 0.95 } : {}}
                disabled={optional ? !(isValid && dirty) : false}
              >
                {!optional ? 'Next' : 'Update Profile'}
              </motion.button>
              {!optional
                ? (
                  <motion.button
                    type='button'
                    className={css(styles.button, styles.backButton)}
                    onClick={onBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Back
                  </motion.button>
                  )
                : null}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    color: '#4a5568'
  },
  checkbox: {
    marginRight: '0.5rem'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '1rem'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  }
});

export default Preferences;
